import {
  Alert,
  Group,
  Loader,
  Pagination,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { CommunicationLogListItem } from "../../../services/admin/communicationLog.service";
import CommunicationLogTableRow from "./CommunicationLogTableRow";

interface CommunicationLogTableProps {
  logs: CommunicationLogListItem[];
  total: number;
  totalPages: number;
  page: number;
  isLoading: boolean;
  isError: boolean;
  onPageChange: (page: number) => void;
  onView: (logId: string) => void;
}

export default function CommunicationLogTable({
  logs,
  total,
  totalPages,
  page,
  isLoading,
  isError,
  onPageChange,
  onView,
}: CommunicationLogTableProps) {
  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {isLoading ? "Loading…" : `${total} communication log entries`}
          </Text>
          {totalPages > 1 && (
            <Group gap="md">
              <Text size="sm" c="dimmed">
                Showing {logs.length} of {total}
              </Text>
              <Pagination
                total={totalPages}
                value={page}
                onChange={onPageChange}
                size="sm"
              />
            </Group>
          )}
        </Group>

        {isError ? (
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="red"
            variant="light"
            title="Could not load communication logs"
            role="alert"
          >
            Refresh the page or adjust the filters and try again.
          </Alert>
        ) : isLoading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <ScrollArea type="auto" offsetScrollbars>
            <Table
              striped
              highlightOnHover
              withTableBorder
              withColumnBorders
              miw={1180}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Sent At</Table.Th>
                  <Table.Th>Channel</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Intended recipient</Table.Th>
                  <Table.Th>Routing</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Case #</Table.Th>
                  <Table.Th>Triggered By</Table.Th>
                  <Table.Th w={60}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {logs.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={9}>
                      <Text ta="center" c="dimmed" py="xl">
                        No communication logs found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  logs.map((log) => (
                    <CommunicationLogTableRow
                      key={log.id}
                      log={log}
                      onView={onView}
                    />
                  ))
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Stack>
    </Paper>
  );
}
